import type { FindingCounts, QuestionnaireSnapshot } from '@nexus/core';
import type { ContactDetails, OrganizationDetails } from '@nexus/mod-crm';
import type { ObjectDetails, ProjectDetails } from '@nexus/mod-projects';

/** Limits of the fields, checked by the server and kept by the forms. */
export const MAX_INSPECTORS = 20;
export const MAX_ATTENDEES = 100;
export const MAX_ATTACHMENTS = 50;
export const ATTENDEE_TEXT_MAX = 200;
export const PARTICIPANTS_MAX = 1000;
export const REMARKS_MAX = 20_000;
/** Notes for an AI draft of a finding. */
export const DRAFT_TEXT_MAX = 4000;

/** Planned, on site, or the report went out ("Protokoll versendet"). */
export const INSPECTION_STATUSES = ['planned', 'in-progress', 'completed'] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

/** A person present at the inspection, by name and function, e.g. the caretaker. */
export interface Attendee {
  name: string;
  role: string | null;
}

/** A PDF of the inspection that goes behind the report, in the order of the list. */
export interface InspectionAttachment {
  /** File of the kernel at the inspection (`fire-safety.inspection`). */
  fileId: string;
  title: string;
}

/** Object of the inspection as the list shows it; `null` when the person may not see objects. */
export interface InspectionObject {
  id: string;
  name: string;
  number: string | null;
  city: string | null;
}

export interface InspectionProject {
  id: string;
  number: string;
  title: string;
}

/** An inspection in the list. */
export interface InspectionListItem {
  id: string;
  objectId: string;
  projectId: string | null;
  object: InspectionObject | null;
  project: InspectionProject | null;
  title: string;
  /** ISO date (YYYY-MM-DD). */
  inspectedOn: string;
  status: InspectionStatus;
  /** When the report went out (ISO 8601), only for `completed`. */
  completedAt: string | null;
  /** Accounts of the company. */
  inspectorIds: string[];
  createdBy: string | null;
  findings: FindingCounts;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** One inspection with everything the page shows. */
export interface Inspection extends InspectionListItem {
  participants: string | null;
  attendees: Attendee[];
  remarks: string | null;
  questionnaireTemplateId: string | null;
  questionnaire: QuestionnaireSnapshot | null;
  attachments: InspectionAttachment[];
  /** The person may change the inspection (write permission and scope). */
  canWrite: boolean;
}

/** An inspection as devices hold it (`data` of the offline source): without names and counts. */
export type InspectionRecord = Omit<Inspection, 'object' | 'project' | 'findings'>;

/**
 * What the report of an inspection needs besides the inspection, `GET …/inspections/:id/report`.
 * Object, project, customer and contact are `null` when the person may not see them.
 */
export interface InspectionReportData {
  object: ObjectDetails | null;
  project: ProjectDetails | null;
  /** Client of the project, otherwise the operator, otherwise the owner of the object. */
  customer: OrganizationDetails | null;
  /** Contact person of the client, only when the customer is the client of the project. */
  contact: ContactDetails | null;
  /** Inspectors in the order of the inspection; `name` is `null` for erased people. */
  inspectors: { id: string; name: string | null }[];
}
